import React, { createContext, useContext, useState, useCallback, useRef, ReactNode, useEffect } from 'react';
import { pollingService } from '../lib/polling';
import { createReadingJob, getReadingJobStatus } from '../lib/api';
import { authenticatedJSONFetch } from '../lib/api';
import type { BirthInfo } from '../types';

/**
 * 分析流程状态
 */
export interface AnalysisFlowState {
  jobId: string | null;
  status: 'idle' | 'creating' | 'polling' | 'skeleton_ready' | 'ai_completed' | 'completed' | 'failed';
  phase: 'queued' | 'skeleton_ready' | 'ai_completed' | 'completed';
  progress: number;
  error: AnalysisError | null;
  result: any | null;
  canRetry: boolean;
  aiGenerationTriggered: boolean; // 🆕 标记是否已触发 AI 生成

  // 详细状态信息（对应 /status 扩展字段）
  reasonCode?: 'TIMEOUT' | 'RATE_LIMIT' | 'INVALID_JSON' | 'EMPTY_FIELD' | 'RESPONSE_TOO_LARGE' | 'SCHEMA_MISMATCH' | 'NETWORK_ERROR' | 'UNKNOWN';
  phaseStartedAt?: string;
  retryAfter?: number;
  aiStatus?: 'pending' | 'generating' | 'success' | 'fallback' | 'failed';
  aiStatusMessage?: string;
}

/**
 * 分析错误
 */
export interface AnalysisError {
  code: 'TIMEOUT' | 'RATE_LIMIT' | 'INVALID_JSON' | 'EMPTY_FIELD' | 'RESPONSE_TOO_LARGE' | 'SCHEMA_MISMATCH' | 'NETWORK_ERROR' | 'UNKNOWN' | 'AUTH_REQUIRED' | 'JOB_NOT_FOUND';
  message: string;
  retryable: boolean;
  retryAfter?: number;
}

/**
 * Context 值类型
 */
interface AnalysisFlowContextValue {
  state: AnalysisFlowState;
  createJob: (birthInfo: BirthInfo) => Promise<string>;
  resumeJob: (jobId: string) => void;
  retryJob: () => Promise<void>;
  clearJob: () => void;
}

const AnalysisFlowContext = createContext<AnalysisFlowContextValue | null>(null);

const initialState: AnalysisFlowState = {
  jobId: null,
  status: 'idle',
  phase: 'queued',
  progress: 0,
  error: null,
  result: null,
  canRetry: false,
  aiGenerationTriggered: false // 🆕 初始化为 false
};

/**
 * 触发 AI 生成（阶段 2）
 */
async function triggerAIGeneration(jobId: string): Promise<void> {
  console.log('[AnalysisFlow] Triggering AI generation for job:', jobId);

  try {
    const response = await authenticatedJSONFetch<{ success: boolean; message?: string }>(
      `/api/analyses/${jobId}/generate-ai`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }
    );

    if (!response.ok) {
      throw new Error(response.error?.message || 'Failed to trigger AI generation');
    }

    console.log('[AnalysisFlow] AI generation triggered successfully');
  } catch (error: any) {
    console.error('[AnalysisFlow] Failed to trigger AI generation:', error);
    throw error;
  }
}

/**
 * AnalysisFlowProvider - 全局单例的分析流程管理
 */
export function AnalysisFlowProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AnalysisFlowState>(initialState);

  // 并发锁：确保同一时间只有一个任务在执行
  const jobLockRef = useRef<string | null>(null);

  // 轮询停止函数
  const stopPollingRef = useRef<(() => void) | null>(null);

  // 🆕 使用 ref 追踪 AI 生成触发状态，避免依赖 React 状态
  // 使用 Set 存储已触发 AI 生成的 Job ID
  const aiGenerationTriggeredRef = useRef<Set<string>>(new Set());

  /**
   * 创建新任务
   */
  const createJob = useCallback(async (birthInfo: BirthInfo): Promise<string> => {
    console.log('[AnalysisFlow] Creating new job...');
    
    // 检查并发锁
    if (jobLockRef.current) {
      const error: AnalysisError = {
        code: 'UNKNOWN',
        message: 'Another job is already in progress',
        retryable: false
      };
      setState(prev => ({ ...prev, error, status: 'failed' }));
      throw new Error(error.message);
    }
    
    try {
      // 新任务开始前清空已触发记录，避免旧 jobId 泄漏
      aiGenerationTriggeredRef.current.clear();

      // 设置创建状态
      jobLockRef.current = 'creating';
      setState(prev => ({
        ...prev,
        status: 'creating',
        error: null,
        progress: 0,
        aiGenerationTriggered: false
      }));
      
      // 调用 API 创建任务
      const response = await createReadingJob({
        birth_date: birthInfo.date,
        birth_time: birthInfo.time,
        birth_location: {
          country: birthInfo.location.split(',')[1]?.trim() || '',
          city: birthInfo.location.split(',')[0]?.trim() || '',
          latitude: birthInfo.latitude,
          longitude: birthInfo.longitude
        },
        timezone: birthInfo.timezone,
        gender: birthInfo.gender,
        language: 'zh'
      });
      
      if (!response.ok || !response.data?.data?.job_id) {
        throw new Error(response.error?.message || 'Failed to create job');
      }
      
      const jobId = response.data.data.job_id;
      console.log('[AnalysisFlow] Job created:', jobId);
      
      // 存储到 sessionStorage
      sessionStorage.setItem('current_job_id', jobId);
      
      // 更新状态并设置锁
      jobLockRef.current = jobId;
      setState(prev => ({
        ...prev,
        jobId,
        status: 'polling',
        progress: 10,
        aiGenerationTriggered: false
      }));
      
      // 立即开始轮询
      startPolling(jobId);
      
      return jobId;
      
    } catch (error: any) {
      console.error('[AnalysisFlow] Failed to create job:', error);
      
      // 清除锁
      jobLockRef.current = null;
      
      // 设置错误状态
      const analysisError: AnalysisError = {
        code: error.code === 'AUTH_REQUIRED' ? 'AUTH_REQUIRED' : 'NETWORK_ERROR',
        message: error.message || 'Failed to create analysis job',
        retryable: error.code !== 'AUTH_REQUIRED'
      };
      
      setState(prev => ({
        ...prev,
        status: 'failed',
        error: analysisError,
        canRetry: analysisError.retryable,
        aiGenerationTriggered: false
      }));
      
      throw error;
    }
  }, []);

  /**
   * 开始轮询任务状态
   */
  const startPolling = useCallback((jobId: string) => {
    console.log('[AnalysisFlow] Starting polling for job:', jobId);
    
    // 停止之前的轮询
    if (stopPollingRef.current) {
      stopPollingRef.current();
    }
    
    // 使用 pollingService 轮询
    const stopFn = pollingService.poll(
      `analysis-flow-${jobId}`,
      async () => {
        const response = await getReadingJobStatus(jobId);
        if (!response.ok) {
          throw new Error('Failed to fetch status');
        }
        return response.data?.data || response.data;
      },
      (statusData, attempt) => {
        console.log(`[AnalysisFlow] Poll attempt ${attempt}:`, {
          status: statusData.status,
          phase: statusData.content_phase,
          progress: statusData.progress
        });
        
        // 更新进度
        setState(prev => ({
          ...prev,
          progress: statusData.progress || prev.progress,
          phase: statusData.content_phase || prev.phase,
          result: statusData.result || prev.result,
          reasonCode: statusData.reason_code,
          phaseStartedAt: statusData.phase_started_at,
          retryAfter: statusData.retry_after,
          aiStatus: statusData.ai_status,
          aiStatusMessage: getAIStatusMessage(statusData.ai_status, statusData.content_phase)
        }));
        
        // 检查是否完成
        if (statusData.content_phase === 'skeleton_ready' && statusData.result) {
          console.log('[AnalysisFlow] Skeleton ready, checking if AI generation needs to be triggered...');

          // 🆕 使用 ref 检查是否已触发，避免依赖 React 状态
          const needsTrigger = !aiGenerationTriggeredRef.current.has(jobId);

          if (needsTrigger) {
            console.log('[AnalysisFlow] First time detecting skeleton_ready, triggering AI generation...');
            console.log('[AnalysisFlow] Job ID:', jobId);

            // 立即标记为已触发，防止重复调用
            aiGenerationTriggeredRef.current.add(jobId);

            // 触发 AI 生成
            triggerAIGeneration(jobId).catch(error => {
              console.error('[AnalysisFlow] Failed to trigger AI generation:', error);
              // 失败时移除标记，允许重试
              aiGenerationTriggeredRef.current.delete(jobId);
            });
          } else {
            console.log('[AnalysisFlow] AI generation already triggered, waiting for completion...');
            console.log('[AnalysisFlow] Triggered jobs:', Array.from(aiGenerationTriggeredRef.current));
          }

          // 更新状态
          setState(prev => ({
            ...prev,
            status: 'skeleton_ready',
            result: statusData.result,
            aiStatusMessage: 'AI 正在生成详细解读...'
          }));

          // ⚠️ 不释放锁，继续轮询等待 ai_completed
          return false; // 继续轮询
        }

        // 🆕 检查 AI 生成完成
        if (statusData.content_phase === 'ai_completed' && statusData.result) {
          console.log('[AnalysisFlow] AI generation completed!');
          setState(prev => ({
            ...prev,
            status: 'ai_completed',
            result: statusData.result,
            aiStatus: 'success',
            aiStatusMessage: 'AI 解读已完成'
          }));
          // ⚠️ 继续轮询等待 status='completed'
          return false;
        }

        if (statusData.status === 'completed') {
          console.log('[AnalysisFlow] Job completed, stopping polling and releasing lock');
          setState(prev => ({
            ...prev,
            status: 'completed',
            result: statusData.result
          }));
          aiGenerationTriggeredRef.current.delete(jobId);
          // ✅ 释放锁，允许创建新任务
          jobLockRef.current = null;
          return true; // 停止轮询
        }

        if (statusData.status === 'failed') {
          console.error('[AnalysisFlow] Job failed:', statusData.error);
          const error: AnalysisError = {
            code: statusData.error_code || 'UNKNOWN',
            message: statusData.error || 'Analysis failed',
            retryable: true
          };
          setState(prev => ({
            ...prev,
            status: 'failed',
            error,
            canRetry: true
          }));
          aiGenerationTriggeredRef.current.delete(jobId);
          // ✅ 释放锁，允许重试或创建新任务
          jobLockRef.current = null;
          return true; // 停止轮询
        }
        
        return false; // 继续轮询
      },
      {
        interval: 2000,
        maxAttempts: 150, // 5 分钟
        onError: (error, attempt) => {
          console.warn(`[AnalysisFlow] Polling error at attempt ${attempt}:`, error);
          
          // 如果是网络错误，继续重试
          if (error.message.includes('fetch') || error.message.includes('network')) {
            return; // 继续轮询
          }
          
          // 其他错误，停止轮询
          const analysisError: AnalysisError = {
            code: 'NETWORK_ERROR',
            message: error.message,
            retryable: true
          };
          setState(prev => ({
            ...prev,
            status: 'failed',
            error: analysisError,
            canRetry: true
          }));
          // ✅ 释放锁，允许重试
          jobLockRef.current = null;
        }
      }
    );
    
    stopPollingRef.current = stopFn;
  }, []);

  /**
   * 恢复现有任务
   */
  const resumeJob = useCallback((jobId: string) => {
    console.log('[AnalysisFlow] Resuming job:', jobId);
    
    // 检查是否已经在处理这个任务
    if (jobLockRef.current === jobId) {
      console.log('[AnalysisFlow] Job already in progress, skipping resume');
      return;
    }
    
    // 设置锁
    jobLockRef.current = jobId;
    
    // 更新状态
    setState(prev => ({
      ...prev,
      jobId,
      status: 'polling',
      error: null,
      aiGenerationTriggered: false
    }));
    // 恢复任务前确保允许重新触发 AI
    aiGenerationTriggeredRef.current.delete(jobId);
    
    // 存储到 sessionStorage
    sessionStorage.setItem('current_job_id', jobId);
    
    // 开始轮询
    startPolling(jobId);
  }, [startPolling]);

  /**
   * 重试失败的任务
   */
  const retryJob = useCallback(async () => {
    console.log('[AnalysisFlow] Retrying job...');
    
    if (!state.jobId) {
      console.error('[AnalysisFlow] No job to retry');
      return;
    }
    
    // 重置错误状态
    setState(prev => ({
      ...prev,
      error: null,
      status: 'polling',
      canRetry: false,
      aiGenerationTriggered: false
    }));
    aiGenerationTriggeredRef.current.delete(state.jobId);
    
    // 重新开始轮询
    startPolling(state.jobId);
  }, [state.jobId, startPolling]);

  /**
   * 清除任务状态
   */
  const clearJob = useCallback(() => {
    console.log('[AnalysisFlow] Clearing job');
    
    // 停止轮询
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }
    
    // 清除锁
    jobLockRef.current = null;
    
    // 清除 sessionStorage
    sessionStorage.removeItem('current_job_id');
    
    // 重置状态
    setState(initialState);
    aiGenerationTriggeredRef.current.clear();
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (stopPollingRef.current) {
        stopPollingRef.current();
      }
    };
  }, []);

  const value: AnalysisFlowContextValue = {
    state,
    createJob,
    resumeJob,
    retryJob,
    clearJob
  };

  return (
    <AnalysisFlowContext.Provider value={value}>
      {children}
    </AnalysisFlowContext.Provider>
  );
}

/**
 * useAnalysisFlow Hook
 */
export function useAnalysisFlow() {
  const context = useContext(AnalysisFlowContext);
  if (!context) {
    throw new Error('useAnalysisFlow must be used within AnalysisFlowProvider');
  }
  return context;
}

/**
 * 获取 AI 状态提示信息
 */
function getAIStatusMessage(aiStatus?: string, contentPhase?: string): string {
  if (contentPhase === 'skeleton_ready') {
    return 'AI 正在生成详细解读...';
  }
  
  if (aiStatus === 'success') {
    return 'AI 解读已完成';
  }
  
  if (aiStatus === 'fallback') {
    return '已使用默认内容';
  }
  
  if (aiStatus === 'failed') {
    return 'AI 生成失败，请重试';
  }
  
  return '';
}
